// Pictures: the shared pre-upload step, welcome-page images, profile photos and
// the per-user libraries that let either be re-used, plus the welcome page
// document itself.
//
// Moved here from firebase-auth.js unchanged. Everything in it keys off
// auth.currentUser rather than the session module, exactly as it did before.
import {
    auth, db, storage,
    updateProfile, doc, setDoc, getDoc,
    storageRef, uploadBytes, getDownloadURL
} from "./firebase-core.js";
import { resizeImageFile, IMAGE_PRESETS } from "../utils/image-resize.js";

// ----- Welcome Page builder -----
// The rich welcome page lives in its OWN document (welcomePages/{tournamentId}),
// not on the tournament doc, so the (frequently re-synced, 1MB-capped) tournament
// document never carries page layout/HTML. Images are uploaded to Storage; only
// their download URL is stored in the block JSON.

// Largest file we will even attempt to decode. Well above any real photo — it
// exists so a mis-picked 500 MB file fails fast with a message instead of
// locking the tab up inside createImageBitmap.
const MAX_SOURCE_BYTES = 40 * 1024 * 1024;

// Shared pre-upload step for every Storage image: reject non-images and absurd
// files, then downscale + re-encode (see js/utils/image-resize.js — this is what
// keeps spectator egress, the app's main running cost, off a cliff). The size
// ceiling is checked on the RESULT, so a 12 MB phone photo that resizes to
// 300 KB now succeeds where it used to be rejected outright.
async function prepareImageForUpload(file, preset) {
    if (!file) throw new Error('No file provided');
    if (!/^image\//.test(file.type || '')) throw new Error(tr('Please choose an image file.'));
    if (file.size > MAX_SOURCE_BYTES) throw new Error(tr('Image is too large (max 10 MB).'));

    const prepared = await resizeImageFile(file, preset);
    // Resizing can bail out and hand back the original (unsupported format, or a
    // decode failure) — the ceiling has to hold either way, since storage.rules
    // would reject the upload anyway and this gives a readable error instead.
    if (prepared.size > preset.maxBytes) throw new Error(tr('Image is too large (max 10 MB).'));
    return prepared;
}

// Upload an image for a welcome page and return its public download URL.
// The URL is also recorded in the uploader's personal image library so they can
// re-use it later (via listMyWelcomeImages) instead of uploading duplicates.
async function uploadWelcomeImage(tournamentId, file) {
    // storage.rules refuses SVG on a public page (an SVG can carry script); say so
    // plainly rather than failing with a permission error after the upload starts.
    if (file && file.type === 'image/svg+xml') {
        throw new Error(tr('SVG images cannot be used on a welcome page. Use PNG, JPEG, GIF or WebP.'));
    }
    const image = await prepareImageForUpload(file, IMAGE_PRESETS.welcome);
    // A tournament still being created has no document for storage.rules to check
    // ownership against, so its pictures go to the uploader's OWN draft folder —
    // never a folder shared with every other account, which anyone could overwrite.
    const uid = auth.currentUser ? auth.currentUser.uid : '';
    const safeId = (!tournamentId || tournamentId === 'draft') ? `draft-${uid}` : String(tournamentId);
    const safeName = String(image.name || 'image').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `welcome-images/${safeId}/${Date.now()}-${safeName}`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, image, { contentType: image.type || 'image/png' });
    const url = await getDownloadURL(sRef);
    await recordWelcomeImage(url, image.name);
    return url;
}

// Make `url` the signed-in user's avatar: write it to BOTH the Firebase Auth
// profile (photoURL — what every avatar render reads) and the user's Firestore
// doc (so it survives a fresh sync and is available server-side). Shared by the
// "upload new" and "reuse an earlier photo" paths.
async function setProfilePhoto(url) {
    if (!auth.currentUser) throw new Error('Not signed in');
    if (!url) throw new Error('No image selected');
    await updateProfile(auth.currentUser, { photoURL: url });
    try {
        await setDoc(doc(db, "users", auth.currentUser.uid), { photoURL: url }, { merge: true });
    } catch (e) {
        // A user-doc mirror failure must not undo a successful photo change —
        // Auth already has the URL and that's what the UI reads.
        console.warn('Could not mirror photoURL to user doc:', e);
    }
    return url;
}

// Upload a new profile picture for the signed-in user, make it their avatar, and
// record it in their reusable photo library so it can be re-selected later without
// re-uploading (same mechanism as welcome-page images). The file lands in
// profile-photos/{uid}/ (owner-only write per storage.rules). Returns the URL.
async function uploadProfilePhoto(file) {
    if (!auth.currentUser) throw new Error('Not signed in');
    const image = await prepareImageForUpload(file, IMAGE_PRESETS.avatar);

    const uid = auth.currentUser.uid;
    const safeName = String(image.name || 'photo').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `profile-photos/${uid}/${Date.now()}-${safeName}`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, image, { contentType: image.type || 'image/png' });
    const url = await getDownloadURL(sRef);

    await setProfilePhoto(url);
    await recordProfilePhoto(url, image.name);
    return url;
}

// Record a profile photo URL in the user's personal library (a `profilePhotos`
// array on their own users/{uid} doc — kept separate from `welcomeImages` so the
// avatar picker only shows avatars, not welcome-page banners). Newest first,
// de-duplicated by URL, capped so the array can't grow without bound.
async function recordProfilePhoto(url, name) {
    try {
        if (!auth.currentUser || !url) return;
        const docRef = doc(db, "users", auth.currentUser.uid);
        const snap = await getDoc(docRef);
        const cur = (snap.exists() && Array.isArray(snap.data().profilePhotos)) ? snap.data().profilePhotos : [];
        const next = [{ url, name: name || '', at: Date.now() }, ...cur.filter(x => x && x.url !== url)].slice(0, 50);
        await setDoc(docRef, { profilePhotos: next }, { merge: true });
    } catch (e) {
        // A library-recording failure must never block the photo change itself.
        console.warn('Could not record profile photo to library:', e);
    }
}

// List the current user's previously uploaded profile photos (newest first), or
// [] if signed out / none. Used by the avatar picker for re-use.
async function listMyProfilePhotos() {
    try {
        if (!auth.currentUser) return [];
        const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
        const arr = (snap.exists() && Array.isArray(snap.data().profilePhotos)) ? snap.data().profilePhotos : [];
        return arr.filter(x => x && x.url);
    } catch (e) {
        console.warn('Could not list profile photos:', e);
        return [];
    }
}

// Record an uploaded image URL in the current user's personal library (stored as
// a `welcomeImages` array on their own users/{uid} doc — no extra security rule
// needed, and merge:true preserves it across the routine user-doc sync). Newest
// first, de-duplicated by URL, capped so the array can't grow without bound.
async function recordWelcomeImage(url, name) {
    try {
        if (!auth.currentUser || !url) return;
        const docRef = doc(db, "users", auth.currentUser.uid);
        const snap = await getDoc(docRef);
        const cur = (snap.exists() && Array.isArray(snap.data().welcomeImages)) ? snap.data().welcomeImages : [];
        const next = [{ url, name: name || '', at: Date.now() }, ...cur.filter(x => x && x.url !== url)].slice(0, 100);
        await setDoc(docRef, { welcomeImages: next }, { merge: true });
    } catch (e) {
        // A library-recording failure must never block the upload itself.
        console.warn('Could not record welcome image to library:', e);
    }
}

// List the current user's previously uploaded welcome-page images (newest first),
// or [] if signed out / none. Used by the Welcome Page image picker for re-use.
async function listMyWelcomeImages() {
    try {
        if (!auth.currentUser) return [];
        const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
        const arr = (snap.exists() && Array.isArray(snap.data().welcomeImages)) ? snap.data().welcomeImages : [];
        return arr.filter(x => x && x.url);
    } catch (e) {
        console.warn('Could not list welcome images:', e);
        return [];
    }
}

// Persist a welcome page. `page` is the canvas model
// ({ version: 2, canvas: {...}, items: [...] }); ownership/visibility are stamped
// so the security rules can authorize read (public/owner) and write (owner).
//
// setDoc replaces the whole document, so a page still in the legacy block format
// (never opened in the editor this session) is written back untouched instead of
// being flattened to an empty canvas.
async function saveWelcomePage(tournamentId, page, opts = {}) {
    if (!tournamentId) throw new Error('Missing tournamentId');
    const docRef = doc(db, "welcomePages", String(tournamentId));
    const payload = {
        tournamentId: String(tournamentId),
        ownerId: opts.ownerId || (auth.currentUser ? auth.currentUser.uid : null),
        isPublic: opts.isPublic === true,
        updatedAt: new Date().toISOString()
    };
    if (page && Array.isArray(page.items)) {
        payload.version = 2;
        payload.canvas = page.canvas || {};
        payload.items = page.items;
    } else if (page && Array.isArray(page.blocks)) {
        payload.blocks = page.blocks;
    } else {
        payload.version = 2;
        payload.canvas = {};
        payload.items = [];
    }
    await setDoc(docRef, payload);
    return payload;
}

// Fetch a welcome page, or null if none exists / not readable.
async function getWelcomePage(tournamentId) {
    if (!tournamentId) return null;
    try {
        const docRef = doc(db, "welcomePages", String(tournamentId));
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) return docSnap.data();
    } catch (error) {
        // A private tournament's welcome page is intentionally unreadable by guests /
        // non-privileged viewers, so permission-denied is EXPECTED here (e.g. every card on
        // the public landing page probes for a page) — don't spam console.error for it.
        if (error && error.code === 'permission-denied') {
            console.debug("Welcome page not readable (private tournament):", tournamentId);
        } else {
            console.error("Error fetching welcome page:", error);
        }
    }
    return null;
}

export {
    prepareImageForUpload,
    uploadWelcomeImage,
    setProfilePhoto,
    uploadProfilePhoto,
    listMyProfilePhotos,
    listMyWelcomeImages,
    saveWelcomePage,
    getWelcomePage
};
